import { Panel } from '@app/components/common/Collapse/Collapse';
import { IDealRoomSectionContent } from '@app/interfaces/deal-room';
import React, { useEffect, useRef, useState } from 'react';
import * as S from '../DealRoomLanding.styles';
import { PriceQuote } from './PriceQuote/PriceQuote';
import { useTranslation } from 'react-i18next';
import { DynamicPriceQuote } from './PriceQuote/DynamicPriceQuote';
import { dealRoomDisabledSectionFiles } from '@app/constants/dealRoom';
import { DescriptionText } from '@app/components/common/DescriptionText/DescriptionText';
import { useSubMenuMouseflow } from '@app/hooks/useSubMenuMouseflow';
import { scrollToSection } from '@app/utils/scrollToSection';

interface DocumentProps {
  section: IDealRoomSectionContent;
}

export const Document: React.FC<DocumentProps> = ({ section }) => {
  const { t } = useTranslation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // The Mouseflow hook reports one section at a time. Kept empty until the
  // reader actually opens something, so the panels that start expanded are not
  // reported as opens they performed.
  const [lastOpened, setLastOpened] = useState<string>('');
  const seededRef = useRef(false);

  useSubMenuMouseflow(lastOpened, (key) => {
    const sub = section.subMenu?.find((s) => s.key === key);
    return sub?.label || sub?.labelKey || key;
  });

  // Every document starts expanded. Seeded once, on the first render that
  // actually has children — re-seeding would reopen panels the reader closed.
  useEffect(() => {
    if (seededRef.current || !section.subMenu?.length) return;
    seededRef.current = true;
    setOpenKeys(section.subMenu.map((subSection) => subSection.key));
  }, [section.subMenu]);

  const getSectionContent = (section: IDealRoomSectionContent) => {
    const isOpen = openKeys.includes(section.key);
    if (section.isExternal) {
      if (!section.file) return null;
      return <PriceQuote open={isOpen} file={section.file} />;
    }
    if (section.isAdditional) {
      const dealRoomSection = dealRoomDisabledSectionFiles.find((s) => s.section === section.key);
      if (dealRoomSection) {
        return (
          <PriceQuote
            file={`${window.location.origin}/pdf/${dealRoomSection.file}`}
            pageStart={dealRoomSection.pageStart}
            pageEnd={dealRoomSection.pageEnd}
            open={isOpen}
          />
        );
      }
    }
    if (section.dealRoomMenu) {
      return (
        <DynamicPriceQuote
          open={isOpen}
          file={(section.dealRoomMenu?.file as string) || ''}
          link={section.dealRoomMenu?.link}
        />
      );
    }
    return <PriceQuote open={isOpen} file={section.file as string} />;
  };

  useEffect(() => {
    /**
     * `scroll` only for a hash the reader navigated to. On mount the landing is
     * already being driven by DealRoomLanding, and scrolling from here too
     * turns it into a race the two settle differently on each load.
     */
    const applyHash = (scroll: boolean) => {
      if (!window.location.hash) return;
      const hashKey = window.location.hash.replace('#', '');

      // check if this section has a subMenu containing the hashKey
      const subMatch = section.subMenu?.find((subSection) => subSection.key === hashKey);

      if (subMatch) {
        setOpenKeys((keys) => (keys.includes(subMatch.key) ? keys : [...keys, subMatch.key]));
        if (scroll) setLastOpened(subMatch.key);
      }
    };

    applyHash(false);

    const onHashChange = () => applyHash(true);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [section.subMenu]);

  useEffect(() => {
    // No delay: scrollToSection re-reads the panel each tick and waits for it
    // to settle, so waiting here only postpones the landing and makes it read
    // as a second, separate jump.
    scrollToSection(lastOpened);
  }, [lastOpened]);

  const onChange = (keys: string | string[]) => {
    const next = Array.isArray(keys) ? keys : [keys];
    setLastOpened(next.find((key) => !openKeys.includes(key)) ?? '');
    setOpenKeys(next);
  };

  return (
    <>
      {(!section.subMenu || section.subMenu.length === 0) && <S.NoData>{t('dealRooms.landing.noDocuments')}</S.NoData>}
      {section.subMenu && section.subMenu.length > 0 && (
        <S.ChildContainer className="child-container">
          <S.ChildCollapse activeKey={openKeys} onChange={onChange}>
            {section?.subMenu?.map((subSection) => (
              <Panel
                id={subSection.key}
                header={
                  <S.PanelHeaderText>
                    <span>{subSection.label ? subSection.label : subSection.labelKey}</span>
                    {subSection.description && (
                      <DescriptionText
                        description={subSection.description}
                        title={subSection.label || subSection.labelKey}
                      />
                    )}
                  </S.PanelHeaderText>
                }
                key={subSection.key}
              >
                {getSectionContent(subSection)}
              </Panel>
            ))}
          </S.ChildCollapse>
        </S.ChildContainer>
      )}
    </>
  );
};
